import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { ApiSuccessResponse } from '@/types/api'

export type JobApplicationStatus =
  'applied' | 'under_review' | 'shortlisted' | 'rejected' | 'withdrawn'

export interface ApplicationStatusHistoryEntry {
  oldStatus: JobApplicationStatus | null
  newStatus: JobApplicationStatus
  changedAt: string
  notes: string | null
}

export interface StudentApplication {
  id: number
  status: JobApplicationStatus
  appliedAt: string
  job: {
    id: number
    title: string
    organizationName: string
    status: string
  }
  statusHistory?: ApplicationStatusHistoryEntry[]
}

async function withCsrf<T>(fn: () => Promise<T>): Promise<T> {
  await ensureCsrfCookie()
  return fn()
}

export const applicationsApi = {
  list: async () => {
    const { data } =
      await apiClient.get<ApiSuccessResponse<StudentApplication[]>>('/students/applications')
    return data.data
  },

  get: async (id: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<StudentApplication>>(
      `/students/applications/${id}`,
    )
    return data.data
  },

  apply: ({ jobId, answers }: { jobId: number; answers?: Record<string, string> }) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<StudentApplication>>(
        `/students/jobs/${jobId}/apply`,
        { answers },
      )
      return data.data
    }),

  withdraw: (id: number) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<StudentApplication>>(
        `/students/applications/${id}/withdraw`,
      )
      return data.data
    }),
}
